import React, { useState, useEffect } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import firebase from 'firebaseInstance';
import staffInviteService from '../../services/staffInvites';
import styles from './styles.module.scss';
import history from 'shared_redux/history';
import * as AccountsSelectors from 'shared_redux/selectors/models/accounts';
import { fetchAccount, signOut } from 'features/navigation/actions';
import { SIGN_OUT_SUCCEEDED } from 'shared_redux/actions';

/**
 * StaffInvite component for handling staff invitations
 */
const StaffInvite = ({ currentAccount, fetchAccount, signOut }) => {
    const { inviteCode } = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [invite, setInvite] = useState(null);
    const [error, setError] = useState(null);
    const [inviteAccepted, setInviteAccepted] = useState(false);
    const [acceptedData, setAcceptedData] = useState(null);
    const [user, setUser] = useState(null);
    const [emailMismatch, setEmailMismatch] = useState(null);
    const [signOutPending, setSignOutPending] = useState(false);
    
    console.log('StaffInvite render - currentAccount:', currentAccount, 'user:', user, 'error:', error, 'invite:', invite, 'loading:', loading);

    // Handle invitation acceptance with promise resolution and rejection
    const handleAcceptInvite = () => {
        setError(null); // Clear any previous errors
        setLoading(true);
        staffInviteService.acceptStaffInvite(inviteCode)
            .then((response) => {
                console.log('Invitation accepted successfully:', response);
                setAcceptedData(response);
                setInviteAccepted(true);
                setLoading(false);
                // Refresh account data to get updated organization info
                fetchAccount();
            })
            .catch(err => {
                console.error('Error accepting invitation:', err);
                if (err.json?.message) {
                    setError(err.json.message);
                } else {
                    setError('Failed to accept invitation. Please try again.');
                }
                setLoading(false);
            });
    };

    // Listen for Firebase auth changes to get Firebase user for acceptance
    useEffect(() => {
        const auth = getAuth(firebase);
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            console.log('Firebase auth state changed:', firebaseUser ? 'User found' : 'No user', firebaseUser?.uid);
            setUser(firebaseUser);
        });
        
        return () => unsubscribe();
    }, []);

    // Monitor signOut completion by watching currentAccount changes
    useEffect(() => {
        if (signOutPending && !currentAccount) {
            // SignOut completed successfully, redirect to login
            setSignOutPending(false);
            window.location.href = `/login?staffInvite=${inviteCode}`;
        }
    }, [signOutPending, currentAccount, inviteCode]);

    useEffect(() => {
        console.log('Fetching staff invite with code:', inviteCode);

        function fetchInvite() {
            setLoading(true);

            staffInviteService.getStaffInviteByCode(inviteCode)
                .then(response => {
                    console.log('Success response:', response);
                    setInvite(response);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Error fetching invite:', err);
                    
                    if (err.statusCode === 404) {
                        setError('Invitation not found or no longer valid.');
                    } else if (err.statusCode === 401) {
                        setError('Authentication required. Please refresh the page and try again.');
                    } else {
                        setError('Unable to load invitation details. The invitation may have expired or been revoked.');
                    }
                    setLoading(false);
                });
        }

        fetchInvite();
    }, [inviteCode]);

    // Check for email mismatch when both account and invite are loaded
    useEffect(() => {
        if (currentAccount && invite && currentAccount.email && invite.email) {
            const currentEmail = currentAccount.email.toLowerCase();
            const inviteEmail = invite.email.toLowerCase();
            
            if (currentEmail !== inviteEmail) {
                setEmailMismatch({
                    currentEmail: currentAccount.email,
                    inviteEmail: invite.email
                });
            } else {
                setEmailMismatch(null);
            }
            
            // Clear any previous errors when we have valid account and invite
            setError(null);
        }
    }, [currentAccount, invite]);

    // Show success page if invite was accepted
    if (inviteAccepted && acceptedData) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.successIcon}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="m9 12 2 2 4-4"/>
                        </svg>
                    </div>
                    
                    <h2>Welcome to the team!</h2>
                    
                    <div className={styles.successMessage}>
                        <p>You've successfully joined <strong>{acceptedData.organization.name}</strong> as a <strong>{getRoleDisplayName(acceptedData.role)}</strong>.</p>
                        <p>You can now access the RepOne coaching platform and start collaborating with your team.</p>
                    </div>
                    
                    <button
                        className={styles.button}
                        onClick={() => history.push('/')}
                    >
                        Go to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.spinner}></div>
                    <p>Loading invitation details...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <h2>Invitation Error</h2>
                    <div className={styles.error}>{error}</div>
                    <button
                        className={styles.button}
                        onClick={() => history.push('/login')}
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    // Authentication error UI - when user is not logged in
    if (!currentAccount) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <h2>Staff Invitation</h2>
                    
                    {invite && (
                        <div className={styles.inviteDetails}>
                            <p><strong>You've been invited to join:</strong></p>
                            <p className={styles.organizationName}>{invite.organization.name}</p>
                            <p><strong>Role:</strong> {getRoleDisplayName(invite.role)}</p>
                            {invite.title && <p><strong>Title:</strong> {invite.title}</p>}
                            {invite.inviter_name && <p><strong>Invited by:</strong> {invite.inviter_name}</p>}
                        </div>
                    )}
                    
                    <div className={styles.rolePermissions}>
                        {invite && (
                            <>
                                <h4>As a {getRoleDisplayName(invite.role)}, you'll be able to:</h4>
                                <ul>
                                    {getRolePermissions(invite.role).map((permission, index) => (
                                        <li key={index}>{permission}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                    
                    <p>You need to sign in or create an account to accept this invitation.</p>
                    
                    <div className={styles.buttonContainer}>
                        <Link
                            to={`/staffSignup?staff_invite=${inviteCode}`}
                            className={`${styles.button} ${styles.primaryButton}`}
                        >
                            Create Account
                        </Link>
                        <Link
                            to={`/login?staffInvite=${inviteCode}`}
                            className={styles.button}
                        >
                            Sign In
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Show email mismatch warning if logged in with different email
    if (emailMismatch && currentAccount) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <h2>Email Mismatch</h2>
                    
                    <div className={styles.warningMessage}>
                        <p>You're currently signed in as <strong>{emailMismatch.currentEmail}</strong>, but this invitation was sent to a different email address.</p>
                        <p>You can still accept this invitation, but it will be linked to your current account.</p>
                    </div>
                    
                    {invite && (
                        <div className={styles.inviteDetails}>
                            <p><strong>You've been invited to join:</strong></p>
                            <p className={styles.organizationName}>{invite.organization.name}</p>
                            <p><strong>Role:</strong> {getRoleDisplayName(invite.role)}</p>
                            {invite.title && <p><strong>Title:</strong> {invite.title}</p>}
                            <p><strong>Invited by:</strong> {invite.inviter_name}</p>
                        </div>
                    )}
                    
                    <div className={styles.buttonContainer}>
                        <button
                            className={styles.button}
                            onClick={() => {
                                console.log('Accept button clicked. Current account:', currentAccount);
                                if (!currentAccount) {
                                    console.error('No current account available');
                                    setError('Authentication error. Please refresh the page and try again.');
                                    return;
                                }
                                handleAcceptInvite();
                            }}
                            disabled={loading}
                        >
                            {loading ? 'Processing...' : 'Accept the invitation anyway'}
                        </button>
                        
                        <button
                            className={`${styles.button} ${styles.secondaryButton}`}
                            onClick={() => {
                                // Use Redux signOut action which handles both backend and Firebase signout
                                setSignOutPending(true);
                                signOut();
                            }}
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Invite found and user is authenticated - show invitation details
    if (invite && currentAccount) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <h2>Staff Invitation</h2>

                    <div className={styles.inviteDetails}>
                        <p><strong>You've been invited to join:</strong></p>
                        <p className={styles.organizationName}>{invite.organization.name}</p>
                        <p><strong>Role:</strong> {getRoleDisplayName(invite.role)}</p>
                        {invite.title && <p><strong>Title:</strong> {invite.title}</p>}
                        <p><strong>Invited by:</strong> {invite.inviter_name}</p>
                    </div>

                    <div className={styles.rolePermissions}>
                        <h4>As a {getRoleDisplayName(invite.role)}, you'll be able to:</h4>
                        <ul>
                            {getRolePermissions(invite.role).map((permission, index) => (
                                <li key={index}>{permission}</li>
                            ))}
                        </ul>
                    </div>

                    <button
                        className={styles.button}
                        onClick={handleAcceptInvite}
                        disabled={loading}
                    >
                        {loading ? 'Processing...' : 'Accept Invitation'}
                    </button>
                </div>
            </div>
        );
    }

    // Fallback - shouldn't hit this normally
    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <h2>Invalid Invitation</h2>
                <p>Sorry, this invitation is no longer valid.</p>
                <button
                    className={styles.button}
                    onClick={() => history.push('/login')}
                >
                    Go to Login
                </button>
            </div>
        </div>
    );
};

// Helper functions
function getRoleDisplayName(role) {
    switch (role) {
        case 'OWNER':
            return 'Owner';
        case 'ADMIN':
            return 'Admin';
        case 'STAFF':
            return 'Staff';
        default:
            return role;
    }
}

function getRolePermissions(role) {
    switch (role) {
        case 'OWNER':
            return [
                'Manage all aspects of the organization',
                'Create and manage programs and sessions',
                'Invite and manage other staff members',
                'View and analyze athlete data',
                'Manage billing and subscription settings'
            ];
        case 'ADMIN':
            return [
                'Create and manage programs and sessions',
                'Invite staff members',
                'View and analyze athlete data',
                'Manage teams and groups'
            ];
        case 'STAFF':
            return [
                'Create and manage programs and sessions',
                'View and analyze athlete data',
                'Work with assigned teams and athletes'
            ];
        default:
            return [];
    }
}

const mapStateToProps = (state) => {
    const currentAccount = AccountsSelectors.getAccount(state);
    return {
        currentAccount
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        fetchAccount: () => dispatch(fetchAccount()),
        signOut: () => dispatch(signOut())
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(StaffInvite);